import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import { useMobileView, useRouteQuery, useSort } from '@/composables';
import FetchData from '@/components/DataProvider/FetchData.vue';
import { GenericTable, TableStyle } from '@/components/Table/__new__';
import { Checkbox, ListHeaderCheckbox } from '@/components/Inputs';
import { useSelection } from '@/composables/use-selection';
import { TablePagination } from '@/views/_components/Table';
import ExpandButton from '@/components/Table/TableGrid/components/ExpandButton.vue';
import { mdiChevronDown } from '@mdi/js';
import { updateObjectImportant } from '@/api/requests/objects';
import StatusToggler from '@/components/StatusToggler/StatusToggler.vue';
import { OBJECT_STATUS_COLORS } from '@/constants/object';
import { Debounce } from '@/constants/debounce';
import { useExpandDetails } from '@/composables';
import TableLoader from '@/views/_components/Table/TableLoader.vue';
import ShowingMenu from '@/components/EllipsisMenu/ShowingMenu.vue';
import { Popover } from '@/components/Popover';
import { Icon } from '@/components/Icon';
import { useObjectListActions } from '@/views/Brand/Objects/_composables/use-object-list-actions';
import { SORT } from '@/constants/filters';
import { StarToggle } from '@/components/StarToggle';
import { ObjectTableExpandedRow } from '@/views/Brand/Objects/_components/object-table-expanded-row';
import { mapQueryToApiV2 } from '@/util/query';
import PlacesListWithModal from '@/views/Brand/Objects/_components/PlacesListWithModal.vue';
import CountListWithPopover from '@/views/Brand/Objects/_components/CountListWithPopover.vue';
import { formatEffectiveSaleMonthlyPrice } from '@/views/Brand/Objects/_utils/effective-sale-monthly-price';
export default defineComponent({
    props: {
        groupId: String,
    },
    components: {
        ObjectTableExpandedRow,
        TableLoader,
        TablePagination,
        Checkbox,
        ListHeaderCheckbox,
        FetchData,
        TableDisplay: GenericTable(),
        ExpandButton,
        StatusToggler,
        Popover,
        ShowingMenu,
        Icon,
        PlacesListWithModal,
        CountListWithPopover,
        StarToggle,
    },
    setup(props, ctx) {
        const { isMobileView } = useMobileView();
        const { changeState, selectedEntries } = useSelection('id');
        const { routeQuery, updateQueryParameter } = useRouteQuery(ctx);
        const { sortConfig, sortBy, sort, setSortBy } = useSort({
            place: 'placeNameFromPlaces',
            placeNo: 'placeNumber',
            costCenter: 'costCenterName',
        });
        const selectAll = ref(false);
        const isGroupObjectsList = !!props.groupId;
        const { objectsListsActions, updatesCounter: objectListActionsUpdatesCounter, } = useObjectListActions({ selectedEntries }, ctx);
        const updatesCounter = computed(() => objectListActionsUpdatesCounter.value);
        const changeImportanceStatus = async (object, value) => {
            await updateObjectImportant(object.id, { isImportant: value });
            objectListActionsUpdatesCounter.value++;
        };
        const onSelectAll = (results) => {
            changeState(results, selectAll.value);
        };
        watch(selectedEntries, (value) => {
            ctx.emit('input', value);
        }, {
            deep: true,
        });
        watch(routeQuery, (value) => {
            if (value.sortBy && sortBy !== value.sortBy) {
                if (value.sortBy === SORT.NAME_ASC ||
                    value.sortBy === SORT.NAME_DESC ||
                    value.sortBy === SORT.DESCRIPTION_DESC ||
                    value.sortBy === SORT.DESCRIPTION_ASC) {
                    setSortBy(SORT.CREATED_AT_DESC);
                }
                else {
                    setSortBy(value.sortBy);
                }
            }
        });
        watch(sortBy, (value) => {
            updateQueryParameter({
                route: ctx.root.$route,
                parameter: 'sortBy',
                value: { value },
            });
        });
        const columns = computed(() => {
            return [
                {
                    key: 'checkbox',
                },
                {
                    key: 'internalNumber',
                    label: ctx.root.$t('object-list-view.object-number').toString(),
                    cellClass: 'font-bold flex items-center justify-center',
                    sortable: true,
                },
                ...(isMobileView.value
                    ? []
                    : [
                        {
                            key: 'placeNo',
                            label: ctx.root.$t('object-list-view.place-no').toString(),
                            cellClass: 'font-bold flex items-center justify-center',
                            sortable: true,
                            hideDefaultContent: true,
                        },
                        {
                            key: 'place',
                            label: ctx.root.$t('object-list-view.place').toString(),
                            cellClass: 'font-bold flex items-center',
                            sortable: true,
                            hideDefaultContent: true,
                        },
                        {
                            key: 'address',
                            label: ctx.root.$t('object-list-view.address').toString(),
                            formatter: (value, row) => `${row.city}, ${value}`,
                            gridColumn: '1fr',
                            cellClass: 'font-bold flex items-center',
                            sortable: true,
                        },
                        {
                            key: 'costCenter',
                            label: ctx.root.$t('object-list-view.cost-center').toString(),
                            cellClass: 'font-bold flex items-center w-70',
                            sortable: true,
                            hideDefaultContent: true,
                        },
                    ]),
                {
                    key: 'saleMonthlyPrice',
                    label: ctx.root.$t('object-list-view.price').toString(),
                    formatter: (_price, row) => formatEffectiveSaleMonthlyPrice(row),
                    sortable: true,
                    gridColumn: isMobileView.value ? '1fr' : 'auto',
                    cellClass: 'flex items-center',
                },
                {
                    key: 'internalStatus',
                    label: isMobileView.value
                        ? ''
                        : ctx.root.$t('object-list-view.status').toString(),
                    cellClass: isMobileView.value
                        ? 'pr-0 flex items-center'
                        : 'flex items-center',
                    sortable: true,
                    hideDefaultContent: true,
                },
            ];
        });
        const objectsQuery = computed(() => {
            const query = mapQueryToApiV2({
                ...routeQuery.value,
                // groupId,
                brandId: ctx.root.$route.params.id,
            });
            if (routeQuery.value.centralCostId) {
                query.distinct = true;
            }
            return query;
        });
        const { toggleExpandedItems: toggleExpandObjectDetails, isExpanded: isObjectExpanded, } = useExpandDetails((object) => object.id);
        const getPlaceItemKey = (item, index) => (item?.place && item.place.id) || String(index);
        const getCentralCostItemKey = (item, index) => (item?.centralCost && item.centralCost.id) || String(index);
        return {
            Debounce,
            updatesCounter,
            objectsQuery,
            columns,
            changeState,
            selectedEntries,
            selectAll,
            onSelectAll,
            sort,
            sortConfig,
            isObjectExpanded,
            toggleExpandObjectDetails,
            OBJECT_STATUS_COLORS,
            isMobileView,
            getPlaceItemKey,
            getCentralCostItemKey,
            mdiChevronDown,
            objectsListsActions,
            TableStyle,
            isGroupObjectsList,
            changeImportanceStatus,
            formatEffectiveSaleMonthlyPrice,
        };
    },
});
